import ImportProcessPollingMixin from './import-process-polling-mixin.js';

/**
 * Consolidated Import Mixin
 * Combines all import functionality into a single, maintainable mixin
 * Eliminates the need for multiple mixins and complex state management
 */
export default {
    mixins: [ImportProcessPollingMixin],
    data() {
        return {
            // Import state
            initial_load_complete: true,
            import_id: null,
            import_meta_data: null,
            import_state: 'new',
            import_definition: null,
            sheet_information: null,
            preview_data: null,

            // UI state
            current_step: 1,

            // Upload state
            excel_file: null,
            uploading: false,
            upload_process_id: null,
            upload_errors: [],

            //Intermediate sheet data
            intermediate_process_errors: [],

            // Preview state
            generating_preview: false,
            preview_ready: false,
            preview_failed: false,
            preview_error_message: '',

            // Import execution state
            importing: false,
            import_process_id: null,
            execution_errors: [],
            import_locked: false,
            import_locked_reason: '',

            // Column mapping state
            can_continue_to_preview: false,
            suggestedMapping: {},
            loading_sheet_information: false,
            loading_sheet_preview: false,

            // Column mapping state
            column_mapping_data: null,
            saving_column_mapping: false,
            intermediate_process_id: null,
            sheet_metadata_map: {},

        };
    },

    computed: {
        /**
         * Import type - must be implemented by child component
         */
        import_type() {
            throw new Error('import_type must be implemented by child component');
        },

        /**
         * Page title
         */
        page_title() {
            if (this.import_id) {
                return `${this.getImportTypeDisplayName()} Import - ${this.import_id}`;
            }
            return `${this.getImportTypeDisplayName()} Import`;
        },

        /**
         * Sheet name from import definition
         */
        sheet_name() {
            if (this.import_definition && this.import_definition.sheet_name) {
                return this.import_definition.sheet_name;
            }
            return null;
        },

        /**
         * Preview table title with sheet name
         */
        preview_table_title() {
            if (this.sheet_name) {
                return `Chosen Sheet to Import: '${this.sheet_name}'`;
            }
            else {
                return `${this.getImportTypeDisplayName()}  Import Preview`;
            }
        },
        has_write_access() {
            // RedChirp staff can write even on real customer accounts (with UI warning elsewhere)
            if (this.$rc.$state.isAuthActorSystemAdmin()) {
                return true;
            }

            // Bulk message recipients imports allow any employee to write
            if (this.import_type === 'bulk-message-recipients') {
                // If user is signed in, they are an employee of their current organization
                return this.$rc.$state.isSignedIn();
            }

            // Other import types require organization admin
            if (this.$rc.$state.isOrganizationAdmin()) {
                return true;
            }
            return false;
        },

        is_read_only_mode() {
            // User is in read-only mode if they have read access but not write access
            // Backend won't return import details if user doesn't have read access
            return !this.has_write_access;
        },

        show_importing_loading_screen() {
            return this.import_state === 'importing';
        },

        should_show_loading_screen() {
            return !this.initial_load_complete || this.show_importing_loading_screen;
        },


        /**
         * Check if import can be cancelled
         */
        can_cancel_import() {
            return ['uploaded', 'preview-ready'].includes(this.import_state);
        },

        /**
         * Check if can proceed to attestation
         */
        can_proceed_to_attestation() {
            return this.preview_ready && !this.has_errors_preventing_import;
        },

        /**
         * Check if has errors preventing import
         */
        has_errors_preventing_import() {
            return this.preview_data?.global_validation_errors?.length > 0;
        },
    },

    methods: {
        /**
         * Initialize component
         */
        initializeComponent() {
            this.import_id = this.$route.params.import_id;
            if (this.import_id) {
                this.initial_load_complete = false;
                //After this completes, initial_load_complete will be set to true
                this.loadExistingImport();
            }
        },

        /**
         * Load existing import data
         * 
         * TODO The API call for this needs to be refactored so it can be better shared for state management.
         */
        loadExistingImport() {
            this.$rc.rest({
                'endpoint': `GET: /api/v1/imports/${this.import_type}/details/${this.$route.params.organization_id}/${this.import_id}`,
                'params': {},
                'on_success': (data) => {
                    this.import_meta_data = data.import_meta_data;
                    this.import_state = data.import_meta_data.state;
                    this.import_process_id = data.import_process_id || null;
                    if (!this.import_process_id) {
                        // No active process
                    }
                    this.initial_load_complete = true;
                    // Load import definition if available (needed for column mapping)
                    if (data.import_meta_data?.raw_import_definition) {
                        this.import_definition = data.import_meta_data.raw_import_definition;
                    }

                    // Load enriched data if available
                    const enriched_data = data.enriched_data;
                    if (enriched_data) {
                        this.preview_data = enriched_data;
                        this.preview_ready = true;
                    }

                    this.handleImportStateTransition();
                    this.importing = this.import_state === 'importing';

                },
                'on_error': (error) => {
                    this.initial_load_complete= true;
                    this.preview_failed = true;
                    this.preview_error_message = `Failed to load import: ${error}`;
                    // Only redirect if this is a critical error (like import not found)
                    // Don't redirect for temporary network issues
                    if (error && (error.toString().includes('not found') || error.toString().includes('404'))) {
                        this.goBackToImportSearch();
                    }
                }
            });
        },

        /**
         * Handle import state transitions
         * This method should be implemented by each import type to handle their specific step logic
         */
        handleImportStateTransition() {
            // This method should be overridden by components using this mixin
            // Each import type has unique step mappings and state handling requirements
            // If you see this comment in the browser console, the component needs to implement handleImportStateTransition
        },

        /**
         * Load preview data from backend
         */
        loadPreviewData() {
            this.generating_preview = true;
            this.preview_failed = false;
            this.preview_ready = false;

            this.$rc.rest({
                'spinner': false,
                'endpoint': `GET: /api/v1/imports/${this.import_type}/details/${this.$route.params.organization_id}/${this.import_id}`,
                'params': {},
                'timeout': 120000,
                'on_success': (data) => {
                    const enriched_data = data.enriched_data;
                    if (enriched_data) {
                        this.preview_data = enriched_data;
                        this.preview_ready = true;
                        this.preview_failed = false;
                    } else {
                        this.onPreviewFailed({ exception: 'No preview data available' });
                    }
                    this.generating_preview = false;
                },
                'on_error': (error) => {
                    const message = this.$rc.getApiErrorMessage(error, 'Failed to load preview data.');
                    this.onPreviewFailed({ exception: `Failed to load preview data: ${message}` });
                }
            });
        },

        /**
         * Handle preview failure
         */
        onPreviewFailed(error) {
            this.generating_preview = false;
            this.preview_failed = true;
            this.preview_error_message = error.exception || error.message || error;
        },

        /**
         * Start preview generation
         */
        startPreviewGeneration() {
            this.loadPreviewData();
        },

        /**
         * Load sheet information
         */
        loadSheetInformation() {
            this.loading_sheet_information = true;

            // If we have a draft mapping choice for header row, send it along so preview sampling matches UI
            const hasHeaderRow = this.column_mapping_data?.has_header_row;

            this.$rc.rest({
                'spinner': false,
                'endpoint': `GET: /api/v1/imports/${this.import_type}/sheet-information/${this.$route.params.organization_id}/${this.import_id}`,
                'params': hasHeaderRow === undefined ? {} : { has_header_row: hasHeaderRow },
                'on_success': (data) => {
                    const metadata_map = {};
                    if (Array.isArray(data.sheet_metadata))
                    {
                        data.sheet_metadata.forEach(meta => {
                            metadata_map[meta.sheet_name] = meta;
                        });
                    }

                    const enriched_sheets = (data.available_sheets || []).map(sheet => {
                        const meta = metadata_map[sheet.sheet_name];
                        if (!meta)
                        {return sheet;}
                        return {
                            ...sheet,
                            total_rows: meta.row_count ?? sheet.total_rows,
                            row_count: meta.row_count ?? sheet.total_rows,
                            column_count: meta.column_count,
                            guardrail_message: meta.guardrail_message || null
                        };
                    });

                    this.sheet_metadata_map = metadata_map;
                    this.sheet_information = {
                        ...data,
                        available_sheets: enriched_sheets
                    };
                    this.loading_sheet_information = false;
                },
                'on_error': (error) => {
                    this.loading_sheet_information = false;
                    this.preview_failed = true;
                    this.intermediate_process_errors = [this.$rc.getApiErrorMessage(error, 'Unable to load sheet information. Please try again.')];
                }
            });
        },

        /**
         * Get import type display name
         */
        getImportTypeDisplayName() {
            //Find all dashes, replace with space, and capitalize letters
            const prettyType = this.import_type.replace(/-/g, ' ');
            return prettyType.replace(/\b\w/g, l => l.toUpperCase());
        },

        /**
         * Go back to import search
         */
        goBackToImportSearch() {
            this.$router.push(`/organizations/${this.$route.params.organization_id}/imports/${this.import_type}/search`);
        },

        /**
         * Navigate to step
         */
        goToStep(step) {
            if (this.canEditStep(step)) {
                this.current_step = step;
            }
        },

        // === UPLOAD FUNCTIONALITY ===

        /**
         * Handle file upload
         */
        onClickUploadFile() {
            this.uploading = true;
            this.upload_errors = [];

            const form_data = new FormData();
            form_data.append(this.import_type + "_file", this.excel_file);
            form_data.append('original_file_name', this.excel_file.name);
            form_data.append('import_type', this.import_type);

            // Add any additional form data
            this.addAdditionalUploadData(form_data);

            this.$rc.makeApiCall({
                url: this.getUploadUrl(),
                method: 'post',
                form_data: form_data,
                timeout: 2 * 60 * 1000, // 2 minutes
                on_response: 'onResponseStandard',
                on_response_params: {
                    on_success: (data) => {
                        this.upload_process_id = data.process_id;
                    },
                    on_error: (error) => {
                        this.uploading = false;
                        this.upload_errors = [this.formatUploadError ? this.formatUploadError(error.message || error) : (error.message || error)];
                        this.preview_failed = true;
                        this.preview_error_message = 'Upload failed. Please review your file and try again.';
                    }
                }
            });
        },

        /**
         * Get upload URL
         */
        getUploadUrl() {
            return `/api/v1/imports/${this.import_type}/upload/${this.$route.params.organization_id}`;
        },

        /**
         * Add additional upload data (override in child components)
         */
        addAdditionalUploadData(/* form_data */) {
            // Default implementation - child components can override
        },



        /**
         * Handle upload completion
         */
        onUploadComplete(result) {
            this.uploading = false;
            this.import_id = result.output.import_id;
            this.import_state = result.output.state;

            // Update URL to include import_id while preserving query parameters
            this.$router.replace({
                name: this.$route.name,
                params: { ...this.$route.params, import_id: this.import_id },
                query: { ...this.$route.query }
            });

            this.loadExistingImport();
        },

        /**
         * Handle upload failure
         */
        onUploadFailed(error) {
            this.uploading = false;
            this.upload_errors = [error.exception || error.message || error];
        },



        // === COLUMN MAPPING FUNCTIONALITY ===

        /**
         * Handle column mapping completion
         */
        onMappingSaved(data) {
            this.import_state = data.state || 'preview-ready';
            this.handleImportStateTransition();
        },

        /**
         * Handle column mapping failure
         */
        onMappingFailed(error) {
            this.$rc.snackbar(`Column mapping failed: ${error}`, 'error');
        },

        /**
         * Handle can continue change
         */
        onCanContinueChanged(canContinue) {
            this.can_continue_to_preview = canContinue;
        },

        getExecutionStepNumber() {
            return 5;
        },

        applyImportInProgressState() {
            this.import_locked = false;
            this.import_locked_reason = '';
            this.current_step = this.getExecutionStepNumber();
            this.importing = true;
            if (!this.preview_ready && !this.generating_preview && !this.preview_failed) {
                this.loadPreviewData();
            }

        },



        // === IMPORT EXECUTION FUNCTIONALITY ===

        /**
         * Execute import
         */
        onExecuteImport() {
            this.importing = true;

            const requestParams = this.getExecutionRequestParams();

            this.$rc.rest({
                'spinner': false,
                'endpoint': `POST: /api/v1/imports/${this.import_type}/execute/${this.$route.params.organization_id}/${this.import_id}`,
                'params': requestParams,
                'on_success': (data) => {
                    this.import_process_id = data.process_id;
                },
                'on_error': (error) => {
                    this.importing = false;
                    this.execution_errors = [this.formatImportError ? this.formatImportError(error) : (error.message || error)];
                }
            });
        },

        /**
         * Get execution request parameters (override in child components)
         */
        getExecutionRequestParams() {
            return {};
        },



        /**
         * Handle execution completion
         */
        onExecutionComplete() {

            this.import_process_id = null;
            this.importing = false;
            this.import_state = 'completed';
            this.current_step = 5;
            this.loadExistingImport();
        },

        /**
         * Handle execution failure
         */
        onExecutionFailed(errorData) {
            this.import_process_id = null;
            this.importing = false;

            // Parse the error - errorData may be a full PTE object, an object with exception field, or a string
            let error_message = 'Failed to execute import';

            if (typeof errorData === 'string')
            {
                error_message = errorData;
            }
            else if (errorData && typeof errorData === 'object')
            {
                // Try to extract the exception field from the PTE object
                if (errorData.exception)
                {
                    error_message = errorData.exception;
                }
                else if (errorData.message)
                {
                    error_message = errorData.message;
                }
            }

            this.execution_errors = [error_message];
        },



        // === UTILITY METHODS ===

        /**
         * Cancel import
         */
        cancelImport() {
            this.$rc.rest({
                'endpoint': `POST: /api/v1/imports/${this.import_type}/cancel/${this.$route.params.organization_id}/${this.import_id}`,
                'params': {},
                'on_success': () => {
                    this.import_state = 'cancelled';
                    this.handleImportStateTransition();

                },
                'on_error': (error) => {
                    this.$rc.alert(`Failed to cancel import: ${error}`, 'error');
                }
            });
        },

        /**
         * Format upload error (override in child components)
         */
        formatUploadError(error) {
            return error;
        },

        /**
         * Format import error (override in child components)
         */
        formatImportError(error) {
            return error;
        },

        // === COLUMN MAPPING METHODS ===

        /**
         * Save column mapping and create intermediate sheet
         */
        saveColumnMapping() {
            if (!this.column_mapping_data) {
                this.$rc.snackbar('Please configure column mapping first', 'error');
                return;
            }

            this.saving_column_mapping = true;

            // Save the mapping definition
            this.$rc.rest({
                'spinner': false,
                'endpoint': `POST: /api/v1/imports/${this.import_type}/update-definition/${this.$route.params.organization_id}/${this.import_id}`,
                'params': {
                    import_definition: this.column_mapping_data
                },
                'on_success': () => {
                    this.createIntermediateSheet();
                },
                'on_error': (error) => {
                    this.saving_column_mapping = false;
                    const message = (error && (error.exception || error.message)) ? (error.exception || error.message) : 'Failed to save column mapping';
                    this.$rc.alert('Failed to save mapping', message);

                }
            });
        },

        /**
         * Create intermediate sheet using generic polling
         */
        createIntermediateSheet() {
            this.$rc.rest({
                'spinner': false,
                'endpoint': `POST: /api/v1/imports/${this.import_type}/create-intermediate/${this.$route.params.organization_id}/${this.import_id}`,
                'params': {},
                'on_success': (data) => {
                    // Use generic polling method
                    this.intermediate_process_id = data.process_id;
                    this.pollGenericProcess(this.intermediate_process_id,
                        this.onIntermediateComplete.bind(this),
                        (error) => this.onIntermediateFailed(error)
                    );
                },
                'on_error': (error) => {
                    this.saving_column_mapping = false;
                    const message = (error && (error.exception || error.message)) ? (error.exception || error.message) : 'Failed to create intermediate sheet';
                    this.intermediate_process_errors = [message];
                    this.current_step = 2;
                    this.$rc.snackbar(`Failed to create intermediate sheet: ${message}`, 'error');
                }
            });
        },

        /**
         * Handle intermediate sheet creation completion
         */
        onIntermediateComplete(data) {
            this.saving_column_mapping = false;
            this.import_state = data.output.state;
            this.onMappingSaved(data.output);
            this.loadExistingImport();
        },

        /**
         * Handle intermediate sheet creation failure
         */
        onIntermediateFailed(error) {
            this.saving_column_mapping = false;
            this.import_state = 'failed';
            const message = (error && (error.exception || error.message)) ? (error.exception || error.message) : (error || 'Failed to create intermediate sheet');
            this.preview_error_message = message;
            this.handleImportStateTransition();
            this.$rc.alert(`Intermediate sheet creation failed`, message);
        },

        /**
         * Handle column mapping data changes from the component
         */
        onColumnMappingChanged(mappingData) {
            this.column_mapping_data = mappingData;
        },

        /**
         * Fetch preview data for a specific sheet when requested by the UI
         */
        fetchSheetPreviewForSheet(sheet_name, header_override) {
            if (!sheet_name || !this.sheet_information || !this.import_id) {
                return;
            }

            const metadata = this.sheet_metadata_map ? this.sheet_metadata_map[sheet_name] : null;
            if (metadata && metadata.guardrail_message) {
                this.intermediate_process_errors = [metadata.guardrail_message];
                return;
            }

            this.loading_sheet_preview = true;

            const query = new URLSearchParams();
            query.set('sheet_name', sheet_name);
            if (typeof header_override === 'boolean') {
                query.set('has_header_row', header_override.toString());
            }

            const endpoint = `GET: /api/v1/imports/${this.import_type}/sheet-preview/${this.$route.params.organization_id}/${this.import_id}?${query.toString()}`;

            this.$rc.rest({
                spinner: false,
                endpoint,
                params: {},
                timeout: 120000,
                on_success: (data) => {
                    this.loading_sheet_preview = false;
                    const sheet_sample = data?.sheet_sample;
                    if (sheet_sample) {
                        this.updateSheetPreviewData(sheet_sample);
                    }
                },
                on_error: (error) => {
                    this.loading_sheet_preview = false;
                    const message = (error && (error.exception || error.message)) ? (error.exception || error.message) : 'Failed to load sheet preview';
                    this.intermediate_process_errors = [message];
                }
            });
        },

        /**
         * Replace the cached sheet entry with the preview data returned from the backend
         */
        updateSheetPreviewData(sheet_sample) {
            if (!sheet_sample || !this.sheet_information || !this.sheet_information.available_sheets) {
                return;
            }

            const meta = this.sheet_metadata_map ? this.sheet_metadata_map[sheet_sample.sheet_name] : null;
            const enriched_sample = {
                ...sheet_sample,
                total_rows: meta?.row_count ?? sheet_sample.total_rows,
                row_count: meta?.row_count ?? sheet_sample.total_rows,
                column_count: meta?.column_count ?? (sheet_sample.column_headers ? sheet_sample.column_headers.length : 0),
                guardrail_message: meta?.guardrail_message || null
            };

            const updatedSheets = this.sheet_information.available_sheets.map(sheet => {
                if (sheet.sheet_name === sheet_sample.sheet_name) {
                    return enriched_sample;
                }
                return sheet;
            });

            this.sheet_information = {
                ...this.sheet_information,
                available_sheets: updatedSheets
            };
        },

        /**
         * Respond to column mapping component sheet selection changes
         */
        onColumnMappingSheetChanged(payload) {
            const sheet_name = payload?.sheet_name;
            const has_header_row = payload?.has_header_row;
            this.fetchSheetPreviewForSheet(sheet_name, has_header_row);
        },

    },
    watch: {
        column_mapping_data: {
            handler(newVal, oldVal) {
                if (!newVal || !oldVal) return;
                if (newVal.has_header_row !== oldVal.has_header_row && this.import_state === 'preview-ready') {
                    this.saveColumnMapping();
                }
            },
            deep: true
        }
    },
};
